import type { ReactElement } from "react";
import { Navigate } from "react-router-dom";
import { Spinner } from "@/shared/components/ui/spinner";
import { useMembershipStatus } from "../services";
import { useProfile } from "@/features/profile/services";
import { resolveRouteForActiveMembership } from "../lib/membership-flow";
import { isKycVerifiedStatus } from "@/features/kyc/utils/kyc-state";

type MembershipGateProps = {
  children: ReactElement;
};

const MembershipGate = ({ children }: MembershipGateProps) => {
  const {
    data: membershipStatus,
    isLoading: isMembershipLoading,
    isError: isMembershipError,
  } = useMembershipStatus({
    retry: 1,
    staleTime: 30_000,
  });

  const {
    data: profile,
    isLoading: isProfileLoading,
    isError: isProfileError,
  } = useProfile({
    staleTime: 30_000,
    retry: 1,
  });

  if (isMembershipLoading || isProfileLoading) {
    return (
      <div className="flex h-full items-center justify-center py-10">
        <div className="flex items-center gap-3 text-sm text-white/70">
          <Spinner className="size-5" />
          Checking membership...
        </div>
      </div>
    );
  }

  if (isMembershipError && isProfileError) {
    return children;
  }

  const hasActiveMembership =
    membershipStatus?.status?.toUpperCase() === "ACTIVE";
  const hasValidPlan = Boolean(profile?.membership?.planName);
  const isKycVerified = isKycVerifiedStatus(profile?.kyc?.status ?? null);

  if (hasActiveMembership && hasValidPlan) {
    return (
      <Navigate
        to={resolveRouteForActiveMembership({ isKycVerified })}
        replace
      />
    );
  }

  return children;
};

export default MembershipGate;
